import React, { forwardRef, useEffect, useRef, useState } from "react"
import {
  MdOutlineKeyboardArrowDown,
  MdOutlineKeyboardArrowUp,
} from "react-icons/md"

import Location from "@fuse/assets/images/img-location.svg"

type TOption = {
  icon: React.ReactNode
  title: string
  subTitle?: string
  onClick: () => void
}

type CompClassType = React.ComponentProps<"div">["className"]

type TDropDown = {
  list: TOption[]
  top?: CompClassType
  customOptionStyle?: CompClassType
  customOptionContainerStyle?: CompClassType
  valueOfState: string
  searchTermvalue: string
  onChangeVal: (val: string) => void
}

const DropDown = forwardRef<HTMLInputElement, TDropDown>(
  (
    {
      top,
      customOptionContainerStyle,
      valueOfState,
      searchTermvalue,
      onChangeVal,
      customOptionStyle,
      list,
    },
    ref,
  ) => {
    const [showDialog, setShowDialog] = useState(false)
    const [isFocused, setIsFocused] = useState<boolean>(false)
    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (isFocused) {
        setShowDialog(true)
        onChangeVal("")
      } else {
        setShowDialog(false)
      }
    }, [isFocused])

    return (
      <div className="w-full relative" ref={containerRef}>
        <div className="flex px-4 py-4 items-center">
          <div className="w-[20px] h-[20px]">
            <img src={Location} className="w-full aspect-square" />
          </div>
          <div className="border-l-[1px] ml-2 border-[white] flex flex-1 items-center">
            <div className="flex-1">
              <div className="text-start ml-2 text-xs font-normal text-[#7F7F7F]">
                Choose location
              </div>
              <div className="flex justify-between">
                <input
                  ref={ref}
                  className="bg-transparent w-full focus:outline-none text-white ml-2"
                  value={
                    valueOfState && !isFocused ? valueOfState : searchTermvalue
                  }
                  onChange={(e) => {
                    onChangeVal(e.target.value)
                  }}
                  placeholder="Add location"
                  onFocus={() => {
                    setIsFocused(true)
                  }}
                  onBlur={() => {
                    // Add a small delay to allow for click events to register
                    if (
                      containerRef.current &&
                      !containerRef.current.contains(document.activeElement)
                    ) {
                      setTimeout(() => {
                        setIsFocused(false)
                      }, 300)
                    }
                  }}
                />
              </div>
            </div>

            <div
              onClick={() => {
                setShowDialog(!showDialog)
                setIsFocused(!isFocused)
              }}
            >
              {showDialog ? (
                <MdOutlineKeyboardArrowUp color="white" size={20} />
              ) : (
                <MdOutlineKeyboardArrowDown color="white" size={20} />
              )}
            </div>
          </div>
        </div>
        <div className={`relative ${top ? top : "top-2"} z-10`}>
          {showDialog ? (
            <div
              className={`absolute bg-[#2c2c2d] rounded-xl w-full ${customOptionContainerStyle} max-h-[300px] overflow-auto`}
              style={{
                backdropFilter: "blur(10px)",
              }}
            >
              {list.map((item, _) => (
                <div
                  key={_}
                  className={`flex gap-4 p-4 hover:cursor-pointer ${
                    list[_ + 1]?.title
                      ? "border-b-[0.5px] border-[#FFFFFF]"
                      : ""
                  }`}
                  onClick={() => {
                    item?.onClick && item?.onClick()
                  }}
                >
                  <div>
                    <div className="flex justify-center items-center w-9 h-9 rounded-full bg-[#7F7F7F66]">
                      {item?.icon}
                    </div>
                  </div>
                  <div className="flex flex-col items-start justify-center">
                    <div
                      className={`text-[#fff] text-left text-base ${customOptionStyle}`}
                    >
                      {item?.title}
                    </div>
                    {item?.subTitle ? (
                      <div className="text-[#FFFFFF80] text-xs font-normal">
                        {item?.subTitle}
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    )
  },
)

export default React.memo(DropDown)
